import { 
  Package, 
  ShoppingCart, 
  RotateCcw, 
  Store,
  TrendingUp,
  Activity,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Layers
} from 'lucide-react';
import CountUp from 'react-countup';
import { useMemo } from 'react';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

import type { ICompanyItemInterface } from '../api/CompanyItems.interface';
import { classifyItemsByType } from '../utils/utils';

type Props = {
  items: ICompanyItemInterface[];
};

const ClassifiedItems = ({ items }: Props) => {
  const classifiedData = classifyItemsByType(items);
  
  // Calculate total items and percentages
  const { totalUniqueItems, totalQuantity, maxQty } = useMemo(() => {
    const uniqueItems = [...new Set(items?.map((item) => item?.ItemID))].length;
    const totalQty = items.reduce((sum, item) => sum + item.Qty, 0);
    const max = Math.max(...classifiedData.map(d => d.totalQty));
    return {
      totalUniqueItems: uniqueItems,
      totalQuantity: totalQty,
      maxQty: max
    };
  }, [items, classifiedData]);

  const getTypeConfig = (type: string) => {
    const trimmedType = type?.trim();
    switch(trimmedType) {
      case 'بيع':
        return {
          icon: <ShoppingCart className="h-3 w-3" />,
          label: 'مبيعات',
          gradient: 'from-emerald-500 to-emerald-600',
          bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
          borderColor: 'border-emerald-200 dark:border-emerald-800',
          textColor: 'text-emerald-700 dark:text-emerald-400',
          badgeColor: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
          arrow: <ArrowUpRight className="h-2.5 w-2.5" />,
          progressColor: 'bg-emerald-500'
        };
      case 'شراء':
        return {
          icon: <Package className="h-3 w-3" />,
          label: 'مشتريات',
          gradient: 'from-blue-500 to-blue-600',
          bgColor: 'bg-blue-50 dark:bg-blue-950/30',
          borderColor: 'border-blue-200 dark:border-blue-800',
          textColor: 'text-blue-700 dark:text-blue-400',
          badgeColor: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
          arrow: <ArrowDownRight className="h-2.5 w-2.5" />,
          progressColor: 'bg-blue-500'
        };
      case 'مرتجع عميل':
        return {
          icon: <RotateCcw className="h-3 w-3" />,
          label: 'مرتجع عميل',
          gradient: 'from-red-500 to-red-600',
          bgColor: 'bg-red-50 dark:bg-red-950/30',
          borderColor: 'border-red-200 dark:border-red-800',
          textColor: 'text-red-700 dark:text-red-400',
          badgeColor: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
          arrow: null,
          progressColor: 'bg-red-500'
        };
      case 'مرتجع مورد':
        return {
          icon: <RotateCcw className="h-3 w-3" />,
          label: 'مرتجع مورد',
          gradient: 'from-orange-500 to-orange-600',
          bgColor: 'bg-orange-50 dark:bg-orange-950/30',
          borderColor: 'border-orange-200 dark:border-orange-800',
          textColor: 'text-orange-700 dark:text-orange-400',
          badgeColor: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
          arrow: null,
          progressColor: 'bg-orange-500'
        };
      default:
        return {
          icon: <Store className="h-3 w-3" />,
          label: type || 'أخرى',
          gradient: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gray-50 dark:bg-gray-950/30',
          borderColor: 'border-gray-200 dark:border-gray-800',
          textColor: 'text-gray-700 dark:text-gray-400',
          badgeColor: 'bg-gray-100 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300',
          arrow: null,
          progressColor: 'bg-gray-500'
        };
    }
  };

  return (
    <div className="w-full space-y-2">
      {/* Compact Header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">تصنيف الحركات</h3>
        </div>
        <Badge 
          variant="outline" 
          className="h-5 px-2 text-[10px] bg-violet-50 dark:bg-violet-950/30 border-violet-300 dark:border-violet-700"
        >
          <Activity className="h-2.5 w-2.5 ml-0.5" />
          إجمالي: {totalQuantity}
        </Badge>
      </div>

      {/* Compact Cards Grid */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
        {/* Total Unique Items Card */}
        <Card className="group relative overflow-hidden border-0 bg-gradient-to-br from-primary/10 to-primary/5 p-2.5 shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <div className="rounded bg-primary/20 p-1">
                <Layers className="h-3 w-3 text-primary" />
              </div>
              <span className="text-[10px] font-medium text-muted-foreground">الأصناف</span>
            </div>
            <TrendingUp className="h-3 w-3 text-primary/40" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-primary">
              <CountUp
                start={0}
                end={totalUniqueItems}
                duration={1.5}
                separator=","
              />
            </span>
            <span className="text-[10px] text-muted-foreground">صنف</span>
          </div>
          <Progress value={100} className="h-0.5 mt-1.5 bg-primary/20">
            <div className="h-full w-full bg-primary rounded-full" />
          </Progress>
        </Card>

        {/* Type Cards - Compact Version */}
        {classifiedData.length > 0 && classifiedData.map(({ type, count, totalQty }) => {
          const config = getTypeConfig(type);
          const percentage = (totalQty / maxQty) * 100;
          const percentOfTotal = Math.round((totalQty / totalQuantity) * 100);
          
          return (
            <Card 
              key={type} 
              className={cn(
                "group relative overflow-hidden border p-2.5 shadow-sm hover:shadow-md transition-all duration-200",
                config.borderColor,
                config.bgColor
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <div className={cn(
                    "rounded p-1 bg-gradient-to-br",
                    config.gradient,
                    "text-white"
                  )}>
                    {config.icon}
                  </div>
                  <span className={cn("text-[10px] font-medium", config.textColor)}>
                    {config.label}
                  </span>
                </div>
                {config.arrow && (
                  <div className={config.textColor}>
                    {config.arrow}
                  </div>
                )}
              </div>
              
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-1">
                  <span className={cn("text-xl font-bold", config.textColor)}>
                    <CountUp
                      start={0}
                      end={totalQty}
                      duration={1.5}
                      separator=","
                    />
                  </span>
                  <span className="text-[10px] text-muted-foreground">قطعة</span>
                </div>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "h-4 px-1 text-[9px] border-0",
                    config.badgeColor
                  )}
                >
                  {percentOfTotal}%
                </Badge>
              </div>
              
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[9px] text-muted-foreground">{count} حركة</span>
                <Progress value={percentage} className="h-0.5 w-12 bg-muted/30">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all duration-500",
                      config.progressColor
                    )}
                    style={{ width: `${percentage}%` }}
                  />
                </Progress>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Compact Statistics Bar */}
      {classifiedData.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-lg bg-muted/20 border border-border/30">
          <span className="text-[10px] text-muted-foreground font-medium">توزيع:</span>
          {classifiedData.map(({ type, totalQty }) => {
            const config = getTypeConfig(type);
            const percent = ((totalQty / totalQuantity) * 100).toFixed(1);
            return (
              <Badge 
                key={type}
                variant="outline" 
                className={cn(
                  "h-4 px-1.5 text-[9px] border gap-1",
                  config.borderColor,
                  config.badgeColor
                )}
              >
                {config.icon}
                <span>{config.label}: {percent}%</span>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassifiedItems;